import { motion } from "framer-motion";
import { Check, FileText, CalendarClock, Unlock } from "lucide-react";
import { useLang } from "../../lib/i18n";

const condIcons = [FileText, CalendarClock, Unlock];

export const Formules = () => {
  const { t } = useLang();
  return (
    <section id="formules" className="bg-[#FAFAFA] py-24 md:py-32" data-testid="formules-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
        >
          <div>
            <p className="font-script text-[#C9A03C] text-2xl mb-4">{t.formules.eyebrow}</p>
            <h2 className="font-serif-lux text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight text-logo-gradient pb-1">
              {t.formules.title}
            </h2>
          </div>
          <p className="text-[#0A192F]/60 font-light max-w-sm leading-relaxed">{t.formules.intro}</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {t.formules.packs.map((p, i) => (
            <motion.article
              key={p.name}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className={`p-10 border transition-colors duration-500 ${
                i === 0
                  ? "bg-[#0A192F] border-[#C9A03C]/40 text-[#F0F0F0]"
                  : "bg-white border-black/5 hover:border-[#C9A03C]/40"
              }`}
              data-testid={`formule-card-${i}`}
            >
              <p className="font-script text-[#C9A03C] text-xl mb-2">{p.tag}</p>
              <h3 className={`font-serif-lux text-2xl font-semibold mb-8 ${i === 0 ? "text-[#F0F0F0]" : "text-[#0A192F]"}`}>
                {p.name}
              </h3>
              <ul className="space-y-3.5">
                {p.items.map((item) => (
                  <li key={item} className={`flex items-start gap-3 text-sm font-light leading-relaxed ${i === 0 ? "text-[#F0F0F0]/75" : "text-[#0A192F]/70"}`}>
                    <Check className="w-4 h-4 text-[#C9A03C] shrink-0 mt-0.5" strokeWidth={2} />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="grid md:grid-cols-12 gap-10 items-center border border-[#C9A03C]/25 bg-white p-8 md:p-12"
          data-testid="formules-conditions"
        >
          <div className="md:col-span-4">
            <div className="aspect-[3/4] overflow-hidden">
              <img src="/img-formules.jpg" alt={t.formules.imgAlt} className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-8">
            <h3 className="font-serif-lux text-2xl md:text-3xl text-[#0A192F] font-semibold mb-6">{t.formules.conditionsTitle}</h3>
            <div className="space-y-5 mb-8">
              {t.formules.conditions.map((text, i) => {
                const Icon = condIcons[i];
                return (
                  <div key={text} className="flex items-start gap-4">
                    <Icon className="w-5 h-5 text-[#C9A03C] mt-0.5 shrink-0" strokeWidth={1.5} />
                    <p className="text-sm text-[#0A192F]/70 font-light leading-relaxed">{text}</p>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-[#C9A03C]/20 pt-6 space-y-3">
              <p className="text-sm text-[#0A192F]/70 font-light leading-relaxed">
                <span className="text-[#C9A03C] font-medium">{t.formules.forfaitTitle}</span> {t.formules.forfait}
              </p>
              <p className="text-xs text-[#0A192F]/45 font-light">{t.formules.note}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
